import React, { useState, useEffect } from 'react';
import { UserPlus, Users, Shield, Edit2, Trash2, Loader2, Check, X, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMISSION_OPTIONS = [
    { key: 'dashboard', label: 'Dashboard', description: 'View dashboard statistics' },
    { key: 'users', label: 'Users', description: 'Manage user accounts' },
    { key: 'subscriptions', label: 'Subscriptions', description: 'Manage subscriptions' },
    { key: 'documents', label: 'Documents', description: 'View & manage documents' },
    { key: 'contacts', label: 'Contacts', description: 'Manage contact inquiries' },
    { key: 'transactions', label: 'Transactions', description: 'View transactions' },
];

const AdminSubAdminsPage = () => {
    const [subAdmins, setSubAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        permissions: {
            dashboard: true,
            users: false,
            subscriptions: false,
            documents: false,
            contacts: false,
            transactions: false,
        }
    });

    const fetchSubAdmins = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('finmar_admin_token');
            const response = await fetch(`${API}/admin/sub-admins`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setSubAdmins(data.sub_admins || []);
            } else if (response.status === 403) {
                toast.error('Permission denied');
            }
        } catch (error) {
            console.error('Failed to fetch sub-admins:', error);
            toast.error('Failed to load sub-admins');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubAdmins();
    }, []);

    const handleCreate = async () => {
        if (!formData.name || !formData.email || !formData.password) {
            toast.error('Please fill all required fields');
            return;
        }
        
        if (formData.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem('finmar_admin_token');
            const response = await fetch(`${API}/admin/sub-admins`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                toast.success('Sub-admin created successfully');
                setShowCreateDialog(false);
                resetForm();
                fetchSubAdmins();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to create sub-admin');
            }
        } catch (error) {
            toast.error('Failed to create sub-admin');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async () => {
        if (!selectedAdmin) return;

        setSaving(true);
        try {
            const token = localStorage.getItem('finmar_admin_token');
            const response = await fetch(`${API}/admin/sub-admins/${selectedAdmin.admin_id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: formData.name,
                    permissions: formData.permissions,
                    is_active: formData.is_active
                })
            });

            if (response.ok) {
                toast.success('Sub-admin updated successfully');
                setShowEditDialog(false);
                fetchSubAdmins();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to update');
            }
        } catch (error) {
            toast.error('Failed to update sub-admin');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (adminId) => {
        if (!window.confirm('Are you sure you want to delete this sub-admin?')) return;

        try {
            const token = localStorage.getItem('finmar_admin_token');
            const response = await fetch(`${API}/admin/sub-admins/${adminId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                toast.success('Sub-admin deleted');
                fetchSubAdmins();
            } else {
                toast.error('Failed to delete');
            }
        } catch (error) {
            toast.error('Failed to delete sub-admin');
        }
    };

    const handleToggleActive = async (admin) => {
        try {
            const token = localStorage.getItem('finmar_admin_token');
            const response = await fetch(`${API}/admin/sub-admins/${admin.admin_id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ is_active: !admin.is_active })
            });

            if (response.ok) {
                toast.success(`Sub-admin ${admin.is_active ? 'deactivated' : 'activated'}`);
                fetchSubAdmins();
            }
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const openEditDialog = (admin) => {
        setSelectedAdmin(admin);
        setFormData({
            name: admin.name,
            email: admin.email,
            password: '',
            permissions: admin.permissions || {},
            is_active: admin.is_active !== false
        });
        setShowEditDialog(true);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            password: '',
            permissions: {
                dashboard: true,
                users: false,
                subscriptions: false,
                documents: false,
                contacts: false,
                transactions: false,
            }
        });
        setSelectedAdmin(null);
    };

    const togglePermission = (key) => {
        setFormData(prev => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [key]: !prev.permissions[key]
            }
        }));
    };

    return (
        <div className="space-y-6" data-testid="admin-subadmins-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-finmar-navy">Sub-Admins</h1>
                    <p className="text-slate-500">Manage sub-administrator accounts and permissions</p>
                </div>
                <Button
                    onClick={() => { resetForm(); setShowCreateDialog(true); }}
                    className="bg-finmar-gold hover:bg-finmar-gold/90 text-finmar-navy"
                    data-testid="create-subadmin-btn"
                >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Sub-Admin
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <Users className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-finmar-navy">{subAdmins.length}</p>
                                <p className="text-sm text-slate-500">Total Sub-Admins</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-green-100 rounded-lg">
                                <Check className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-finmar-navy">
                                    {subAdmins.filter(a => a.is_active !== false).length}
                                </p>
                                <p className="text-sm text-slate-500">Active</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-slate-100 rounded-lg">
                                <X className="w-6 h-6 text-slate-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-finmar-navy">
                                    {subAdmins.filter(a => a.is_active === false).length}
                                </p>
                                <p className="text-sm text-slate-500">Inactive</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Sub-Admins Table */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>All Sub-Admins</CardTitle>
                    <Button variant="ghost" size="sm" onClick={fetchSubAdmins}>
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-finmar-gold" />
                        </div>
                    ) : subAdmins.length === 0 ? (
                        <div className="text-center py-12">
                            <Shield className="w-16 h-16 mx-auto text-slate-200 mb-4" />
                            <p className="text-slate-500">No sub-admins created yet</p>
                            <Button
                                variant="outline"
                                className="mt-4"
                                onClick={() => { resetForm(); setShowCreateDialog(true); }}
                            >
                                Create First Sub-Admin
                            </Button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-slate-100">
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Name</th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Email</th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Permissions</th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Created</th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subAdmins.map(admin => (
                                        <tr key={admin.admin_id} className="border-b border-slate-50 hover:bg-slate-50">
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-finmar-navy/10 rounded-full flex items-center justify-center">
                                                        <Shield className="w-5 h-5 text-finmar-navy" />
                                                    </div>
                                                    <span className="font-medium text-finmar-navy">{admin.name}</span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-slate-600">{admin.email}</td>
                                            <td className="py-3 px-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {admin.permissions && Object.entries(admin.permissions)
                                                        .filter(([_, v]) => v)
                                                        .map(([key]) => (
                                                            <span key={key} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded capitalize">
                                                                {key}
                                                            </span>
                                                        ))
                                                    }
                                                </div>
                                            </td>
                                            <td className="py-3 px-4">
                                                <span className={`px-2 py-1 rounded-full text-xs ${admin.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                                    {admin.is_active !== false ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-sm text-slate-500">
                                                {new Date(admin.created_at).toLocaleDateString()}
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-1">
                                                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(admin)}>
                                                        <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleToggleActive(admin)}
                                                        title={admin.is_active !== false ? 'Deactivate' : 'Activate'}
                                                    >
                                                        {admin.is_active !== false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleDelete(admin.admin_id)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-finmar-gold" />
                            Create Sub-Admin
                        </DialogTitle>
                        <DialogDescription>
                            Add a new sub-administrator with specific permissions
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Name <span className="text-red-500">*</span></Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Admin name"
                                    data-testid="subadmin-name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Email <span className="text-red-500">*</span></Label>
                                <Input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="admin@example.com"
                                    data-testid="subadmin-email"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Password <span className="text-red-500">*</span></Label>
                            <div className="relative">
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    placeholder="Min 6 characters"
                                    data-testid="subadmin-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <Label>Permissions</Label>
                            <div className="grid grid-cols-2 gap-3">
                                {PERMISSION_OPTIONS.map(perm => (
                                    <div
                                        key={perm.key}
                                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${formData.permissions[perm.key] ? 'border-finmar-gold bg-finmar-gold/5' : 'border-slate-200'}`}
                                        onClick={() => togglePermission(perm.key)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium text-sm">{perm.label}</span>
                                            <Switch checked={formData.permissions[perm.key]} />
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1">{perm.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                        <Button
                            onClick={handleCreate}
                            disabled={saving}
                            className="bg-finmar-gold hover:bg-finmar-gold/90 text-finmar-navy"
                        >
                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                            Create Sub-Admin
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit2 className="w-5 h-5 text-finmar-gold" />
                            Edit Sub-Admin
                        </DialogTitle>
                        <DialogDescription>
                            Update permissions and details for {selectedAdmin?.name}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Name</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input value={formData.email} disabled className="bg-slate-50" />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                                <p className="font-medium">Account Status</p>
                                <p className="text-sm text-slate-500">Enable or disable this sub-admin</p>
                            </div>
                            <Switch
                                checked={formData.is_active}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                            />
                        </div>

                        <div className="space-y-3">
                            <Label>Permissions</Label>
                            <div className="grid grid-cols-2 gap-3">
                                {PERMISSION_OPTIONS.map(perm => (
                                    <div
                                        key={perm.key}
                                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${formData.permissions[perm.key] ? 'border-finmar-gold bg-finmar-gold/5' : 'border-slate-200'}`}
                                        onClick={() => togglePermission(perm.key)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium text-sm">{perm.label}</span>
                                            <Switch checked={formData.permissions[perm.key]} />
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1">{perm.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
                        <Button
                            onClick={handleUpdate}
                            disabled={saving}
                            className="bg-finmar-gold hover:bg-finmar-gold/90 text-finmar-navy"
                        >
                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminSubAdminsPage;
